import { useState, useCallback, useEffect } from 'react';
import { api, BuilderSessionSummary } from '@/lib/api';
import { useNavigate } from 'react-router-dom';

interface UseBuilderSessionsReturn {
  sessions: BuilderSessionSummary[];
  isLoading: boolean;
  isCreating: boolean;
  error: Error | null;
  createSession: (params?: {
    templateId?: string;
    existingArchId?: string;
  }) => Promise<string | null>;
  deleteSession: (sessionId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useBuilderSessions(): UseBuilderSessionsReturn {
  const [sessions, setSessions] = useState<BuilderSessionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.listBuilderSessions();
      setSessions(response.sessions);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch sessions');
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const createSession = useCallback(
    async (params?: { templateId?: string; existingArchId?: string }): Promise<string | null> => {
      setIsCreating(true);
      setError(null);

      try {
        const response = await api.createBuilderSession(params);
        // Navigate to the new session
        navigate(`/a/arche_builder/${response.sessionId}`);
        return response.sessionId;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create session');
        setError(error);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [navigate],
  );

  const deleteSession = useCallback(async (sessionId: string): Promise<boolean> => {
    setError(null);

    try {
      await api.deleteBuilderSession(sessionId);
      // Remove from local state
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete session');
      setError(error);
      return false;
    }
  }, []);

  return {
    sessions,
    isLoading,
    isCreating,
    error,
    createSession,
    deleteSession,
    refresh: fetchSessions,
  };
}
